import { apiClient } from '@/shared/api/http/client';
import type { ChatAttachment, ChatContact, ChatConversation, ChatGroupDetail, ChatMessage, ChatReaction } from '../model/types';

const base = '/api/chat';
const quiet = { octo: { expectSuccess: false } } as const;
type Target = { peerUserId?: string; groupId?: string };

export const chatApi = {
  contacts: () => apiClient.get<ChatContact[]>(`${base}/contacts`),
  conversations: () => apiClient.get<ChatConversation[]>(`${base}/conversations`),
  unread: () => apiClient.get<{ count: number }>(`${base}/unread`, quiet),
  messages: (t: Target, before?: string, take = 40) =>
    apiClient.get<{ items: ChatMessage[]; hasMore: boolean }>(`${base}/messages`, {
      params: { peer: t.peerUserId, group: t.groupId, before, take },
    }),
  send: (body: Target & { content: string; type?: string; payload?: string; replyToId?: string | null; attachmentIds?: string[] }) =>
    apiClient.post<ChatMessage>(`${base}/send`, body),
  edit: (messageId: string, content: string) => apiClient.put<ChatMessage>(`${base}/messages/${messageId}`, { content }),
  remove: (messageId: string) => apiClient.delete<ChatMessage>(`${base}/messages/${messageId}`),
  react: (messageId: string, emoji: string) =>
    apiClient.post<ChatReaction[]>(`${base}/messages/${messageId}/reactions`, { emoji }),
  read: (t: Target) => apiClient.post<{ count: number }>(`${base}/read`, t, quiet),
  typing: (t: Target) => apiClient.post(`${base}/typing`, t, quiet),
  search: (q: string) => apiClient.get<ChatMessage[]>(`${base}/search`, { params: { q } }),
  upload: (file: File, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<ChatAttachment>(`${base}/attachments`, form, {
      onUploadProgress: (e) => e.total && onProgress?.(Math.round((e.loaded / e.total) * 100)),
    });
  },
  /** Ek içeriği (kimlik başlığıyla) — <img> doğrudan yetkili istek atamadığı için blob URL'e çevrilir. */
  attachmentBlob: (url: string) => apiClient.getBlob(url),
  createGroup: (name: string, memberUserIds: string[]) =>
    apiClient.post<ChatGroupDetail>(`${base}/groups`, { name, memberUserIds }),
  group: (groupId: string) => apiClient.get<ChatGroupDetail>(`${base}/groups/${groupId}`),
  renameGroup: (groupId: string, name: string) => apiClient.put<ChatGroupDetail>(`${base}/groups/${groupId}`, { name }),
  addMembers: (groupId: string, memberUserIds: string[]) =>
    apiClient.post<ChatGroupDetail>(`${base}/groups/${groupId}/members`, { memberUserIds }),
  removeMember: (groupId: string, userId: string) => apiClient.delete(`${base}/groups/${groupId}/members/${userId}`),
};
